import React from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { WelcomeIcon } from '@/components/ui/SvgIcon';
import Typography from '@/components/ui/typography';
import { RootState } from '@/store/store';
import { Home, Search } from 'lucide-react';
import { useSelector } from 'react-redux';

export interface NavbarProps {
  title?: string;
  showSearch?: boolean;
  showAvatar?: boolean;
  showWelcomeMessage?: boolean;
  icon?: React.ReactNode;
  onSearch?: (value: string) => void;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  className?: string;
  children?: React.ReactNode;
}

const Navbar: React.FC<NavbarProps> = ({
  title,
  showSearch = true,
  showWelcomeMessage = false,
  icon,
  onSearch,
  className,
  children,
}) => {
  const defaultIcon = icon || (
    <Home className="h-4 w-4 text-gray-600 dark:text-gray-400" />
  );

  const { user } = useSelector((state: RootState) => state.user);

  return (
    <header
      className={cn(
        'flex items-center gap-4 px-6 pb-6 pt-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700',
        className,
      )}
    >
      {/* Left Section */}
      <div className="flex flex-1 items-center gap-4">
        {showWelcomeMessage && (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <img src={WelcomeIcon} alt="Welcome" className="h-10 w-10" />
            </div>
            <Typography variant="p" color="neutral700" weight="bold" size="22">
              Welcome back,{' '}
              {user?.firstName || user?.email?.split('@')[0] || 'User'}
            </Typography>
          </div>
        )}

        {title && (
          <div className="flex items-center gap-2">
            {!showWelcomeMessage && (
              <div className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 bg-white">
                {defaultIcon}
              </div>
            )}
            <Typography variant="p" color="neutral700" weight="bold" size="22">
              {title}
            </Typography>
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {showSearch && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search..."
              className="pl-9 w-64"
              onChange={(e) => onSearch?.(e.target.value)}
            />
          </div>
        )}

        {children}
      </div>
    </header>
  );
};

export default Navbar;
